
import React from "react";
import { i18nManager } from "../../../../../i18n";
import { ApiResponseData, BaseService, CommunityService, ICountry, ICurrency, ICommunity, IPlatform, SanitizedResponse, ConsusService } from "../../../../../services";
import { NavLink, useSearchParams } from "react-router-dom";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../../shared";
import { Schemes, Subscriber, Utils } from "../../../../../utils";
import { FileInput, Alignment, ButtonManageRef, ObjectHelper, Scheme, Button, fileInputFileFromUrl } from "@rivtn/noseur";

export interface CommunitiesProps {
    platform: IPlatform;
}

function Communities(props: CommunitiesProps) {
    const consusService = ConsusService.getInstance();
    const communityService = CommunityService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [communities, setCommunities] = React.useState<ApiResponseData<ICommunity>>({});
    const labels = i18nManager.Labels.dashboard.platform_management.management.support.communities;
    labels.sided_labels.dropdown_form![0].control.options = [{ label: i18nManager.Labels.common.all, value: "" }, ...i18nManager.Languages];

    return SidedContainer({
        searchParams,
        setSearchParams,
        data: communities,
        onAction: manageCommunity,
        queryData: fetchCommunities,
        labels: labels.sided_labels,
        firstStyle: {
            marginTop: 15,
            padding: "0px 20px 20px",
            border: "1px solid #F0F0F0",
        },
        secondStyle: {
            padding: 15,
            marginTop: 15,
            border: "1px solid #F0F0F0",
        },
        dataTemplate: (community: ICommunity) => {
            return (<div className="hera-item" onClick={() => {
                manageCommunity(community);
                Utils.updateSearchParams("adi", community.external_id, setSearchParams);
            }}>
                <img alt={community.url_location} src={community.icon_location} />
                <div>
                    <div className="title lined-limit">{community.url_location} <span> - {i18nManager.Languages.find((l) => l.value == community.language)?.label} ({community.language})</span></div>
                    <div className="content lined-limit">{community.title}</div>
                </div>
            </div>);
        },
    } as any);

    function fetchCommunities() {
        communityService.queryCommunities(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ICommunity>) => {
            setCommunities(apiResponse.data!);
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCommunity(community: ICommunity = {} as any) {
        let selectedFile: File | undefined;
        const isUpdate = !!community.url_location;
        const dialogLabels = labels.dialogs.add_edit;
        community.language = community.language ?? searchParams.get("language");

        const onFormInput = (name: string, value: any) => {
            dialogErrorRef.current!.innerText = " ";
            (community as any)[name] = value;
            return false;
        }
        const dialog = ConfirmDialog({
            closable: true,
            title: (isUpdate ? dialogLabels.edit_title : dialogLabels.add_title),
            confirmLabel: (isUpdate ? dialogLabels.update_action_label : dialogLabels.add_action_label),
            style: { minWidth: 500, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm([dialogLabels.form[0]], onFormInput, community, undefined, { marginTop: 0 }, {
                    "language": i18nManager.Languages
                })}
                <div style={{ display: "flex", marginTop: 15 }}>
                    <FileInput className="pic" stickyPreview rounded scheme={Schemes.RIVTN_QUIRINUS} accepts="image/*"
                        defaultFiles={community.icon_location ? [fileInputFileFromUrl(community.icon_location, "sname.png", `image/*`)] : undefined}
                        style={{ height: 60, width: 60, marginBottom: 0 }} control={<i className='fa fa-pen' style={{ cursor: "pointer" }} />}
                        attrsRelay={{ control: { style: { borderRadius: "50%" }, alignment: Alignment.BOTTOM_RIGHT } }} onSelectFiles={(files) => {
                            selectedFile = files[0];
                            if (!isUpdate) community.icon_location = "https://temp.png";
                        }} onRemoveFile={() => {
                            selectedFile = undefined;
                        }} />
                    {Elements.buildConfirmDialogForm([dialogLabels.form[1]], onFormInput, community, undefined, { marginTop: 0, flex: 1, marginLeft: 20 }, undefined, { marginTop: 5 })}
                </div>
                {Elements.buildConfirmDialogForm([dialogLabels.form[2]], onFormInput, community, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            footerElements: (isUpdate ? (<Button scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 30px" }} onClick={() => {
                dialog.hide(() => deleteCommunity(community));
            }} />) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(community).length < 4) {
                    dialogErrorRef.current!.innerText = dialogLabels.fill_required_fields;
                    return false;
                }
                button?.setLoadingState(true);
                community.platform_id = props.platform.platform_id;
                const method = (isUpdate ? communityService.updateCommunity : communityService.createCommunity).bind(communityService);
                method(community).then(({ apiResponse: { data: community } }: SanitizedResponse<ICommunity>) => {
                    const done = () => {
                        Siren.alert(isUpdate ? dialogLabels.update_successful : dialogLabels.add_successful);
                        dialog.hide(fetchCommunities);
                    };
                    consusService.uploadBinary({ file: selectedFile, fullname: `/support/cat/${community!.external_id}` }).then(async ({ apiResponse: { data } }: SanitizedResponse<any>) => {
                        if (!isUpdate && community) {
                            community.platform_id = props.platform.platform_id;
                            community.icon_location = data.consus_download_url;
                            await communityService.updateCommunity(community as ICommunity);
                        }
                    }).catch(BaseService.reportError).finally(done);
                }).catch(({ errors }: SanitizedResponse<any>) => {
                    dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                }).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

    function deleteCommunity(community: ICommunity) {
        const dialog = ConfirmDialog({
            confirmScheme: Scheme.DANGER,
            title: labels.dialogs.delete.title,
            cancelLabel: i18nManager.Labels.common.cancel,
            desc: labels.dialogs.delete.desc.replace("${url_location}", community.url_location)
                .replace("${language}", community.language),
            onCancel: () => {
                dialog.hide(() => manageCommunity(community));
            },
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                consusService.deleteFiles(["/administrator/" + community.icon_location.substring(community.icon_location.indexOf("/storage/o/") + 11)]).catch(BaseService.reportError);
                communityService.deleteCommunity(community.external_id).then(({ }: SanitizedResponse<any>) => {
                    Siren.alert(labels.dialogs.delete.success);
                    dialog.hide(fetchCommunities);
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show();
    }

}

export default Communities;
